/* ============================================================
   OBED PADILLA — FEED DATA (data.js)
   ------------------------------------------------------------
   This file is the ONLY place content lives. Everything the
   feed renders comes from these arrays, so a Shopify developer
   can replace this entire file with live data without touching
   feed logic or styling.

   SHOPIFY MIGRATION NOTES
   -----------------------
   • FEED_ITEMS  → replace with a metaobject list ("feed_video")
     or a JSON metafield on the shop. Each entry only needs a
     Vimeo ID + category + caption. In Liquid you would render
     this array with:  {{ shop.metaobjects.feed_video | json }}
     into a <script> tag, or fetch it via the Storefront API.
   • PRODUCTS    → replace with real Shopify products. Every
     field below maps 1:1 to Storefront API fields (noted
     inline). The `variants` array maps to product variants
     (size options) and each variant's `id` should become the
     Storefront `variantId` (gid://shopify/ProductVariant/...)
     used by the Buy button in interactions.js.
   • VIMEO IDS   → every `vimeoId` below is a PLACEHOLDER
     (public demo videos so the prototype actually plays).
     Swap in Obed's real Vimeo video IDs — nothing else needs
     to change. Vertical (9:16) uploads will look best; the
     player cover-crops any aspect ratio.
   ============================================================ */

/* ---- Asset path bridge (Shopify theme compatibility) --------------
   On Netlify/static hosting, assets live at their relative paths
   (assets/icons/trending.png). On a Shopify theme, assets are FLAT
   on the CDN, so the theme's Liquid section sets
   window.THEME_ASSET_BASE and files are renamed with the folder
   flattened into the name: assets/icons/trending.png →
   feed-icons-trending.png. Every JS asset reference goes through
   ASSET() so the SAME code runs in both worlds untouched.        */
function ASSET(path) {
  if (window.THEME_ASSET_BASE) {
    return window.THEME_ASSET_BASE +
      'feed-' + path.replace(/^assets\//, '').replace(/\//g, '-');
  }
  return path;
}

/* ---- Global config ------------------------------------------------ */
const CONFIG = {
  artistHandle: '@obedpadilla_',      // mirrors Obed's handle across socials
  artistName: 'Obed Padilla',
  /* MERCH MODE (Benton, 2026-09-02):
     'forward' — every merch tap (photo, size chip, add button, bag
                 rail icon, tap-to-shop) hands the fan straight to
                 that product's page on obedpadilla.com for the
                 traditional store experience. A picked size carries
                 over (?variant=) so it's preselected on arrival.
                 The in-feed cart/checkout UI is disabled.
     'infeed'  — the original in-feed flow: size + add-to-bag in the
                 slide, cart drawer, checkout permalink. Flip back
                 anytime by changing this one value.               */
  merchMode: 'forward',
  // Link used by the Share button. Point at the production domain later.
  shareBaseUrl: 'https://obedpadilla.com/feed',
  /* LAYLO — Obed's mailing list. The fan wall (comment unlock + the
     Backstage Pass tab) is powered by Laylo signup.
     • Set `username` to Obed's real Laylo username and interactions.js
       will swap the mock form for the live embed
       (https://embed.laylo.com/{username}). While it's the placeholder
       value below, the prototype shows a styled mock signup instead so
       it's fully clickable offline.
     • The extra fields the mock captures (name, Instagram, TikTok)
       should be configured as Laylo custom signup questions so the
       real drop collects the same data.
     • Verifying "already signed up" fans: check the email against the
       Laylo audience via their API/webhooks server-side, then set a
       signed cookie. See submitFanForm()/confirmLaylo() in
       interactions.js.                                              */
  laylo: {
    username: 'obedpadilla_',                       // REAL account
    profileUrl: 'https://laylo.com/obedpadilla_',   // fan-facing page
    /* Note: laylo.com blocks iframing (X-Frame-Options) and
       embed.laylo.com is per-drop, so the site's own form is the
       primary signup — production posts it to Laylo's
       subscribeToUser API server-side (interactions.js note). */
  },
  /* BANDSINTOWN — powers the "On The Road" card in the Tour tab.
     Dates are fetched live on every page load, so posting a show
     to Bandsintown updates the site automatically.
     appId 'squarespace' is a working PLACEHOLDER — register a
     proper app_id with Bandsintown for production (bandsintown.js
     has the details).                                            */
  bandsintown: {
    artistId: '13501327',              // bandsintown.com/a/13501327-obed-padilla
    appId: 'squarespace',              // PLACEHOLDER app_id
  },
  // Videos longer than this (seconds) are treated as "full-length"
  // content: they get a FULL SET badge and a seekable progress bar
  // is emphasized so fans can scrub.
  longFormThresholdSec: 300,
};

/* ---- Albums (sub-navigation under the Albums bubble) -------------- */
/* Tapping the Albums bubble opens this second row of bubbles.
   Each entry filters the feed to items with a matching `album` key.
   `mark` is the 1–2 character monogram shown in the bubble (swap for
   real cover art later — see the SHOPIFY note: store cover images as
   metaobject fields and render <img> instead of the mark).          */
const ALBUMS = [
  /* `cover` = album art (assets/album-covers/). The Rodeo Clown EP
     cover is real; drop the rest in the Drive assets folder.      */
  { key: 'rc2',      title: 'Rodeo Clown Act II',                     short: 'Rodeo Clown II',   mark: 'II', cover: 'assets/album-covers/rodeo-clown.jpg' },
  { key: 'rc1',      title: 'Rodeo Clown Act I',                      short: 'Rodeo Clown I',    mark: 'I',  cover: 'assets/album-covers/rodeo-clown.jpg' },
  { key: 'dyslm',    title: 'Do You Still Love Me',                   short: 'Do You Still Love Me', mark: 'DY' },
  { key: '2types',   title: 'They Told Us There’s 2 Types of Mexicans', short: '2 Types of Mexicans', mark: '2T' },
  { key: 'adl',      title: 'A Different Light',                      short: 'A Different Light', mark: 'AL' },
  { key: 'cthd',     title: 'Cheap Thrills & Hard Drugs',             short: 'Cheap Thrills',    mark: 'CT' },
  { key: 'deyssler', title: 'Deyssler',                               short: 'Deyssler',         mark: 'DE' },
  { key: 'tried',    title: 'Tried & Truth',                          short: 'Tried & Truth',    mark: 'TT' },
  { key: 'singles',  title: 'Singles',                                short: 'Singles',          mark: '♪'  },
];

/* ---- Feed items ---------------------------------------------------
   MOVED → js/feed-items.js (a GENERATED file).
   The feed is scripted in the "Obed Feed Script" Google Sheet — one
   row per slide (order, tab, video link, caption, status). Editing
   happens in the sheet; tools/sync_feed.py regenerates
   js/feed-items.js from it. Do not define FEED_ITEMS here.
   ------------------------------------------------------------------ */

/* ---- Products -----------------------------------------------------
   MOVED → js/products.js (GENERATED from Obed's LIVE Shopify store,
   obedpadilla.com — real titles, prices, sizes, stock, photos, and
   variant IDs). Refresh any time with: python3 tools/sync_store.py
   ------------------------------------------------------------------ */

/* ---- Seed comments (prototype only) ------------------------------- */
/* In production, comments would live in a backend / Shopify app
   (e.g. an app proxy endpoint). Seeded here so the drawer feels real. */
const SEED_COMMENTS = {};   // cleared for launch (Benton, 2026-09-15)
// Real comments arrive with the Phase 2 backend.

/* ---- Story bubbles / filters -------------------------------------- */
/* Tapping a bubble FILTERS the feed in place (no navigation).
   `all` is the default resting state.                              */
/* The row starts with Trending — the site's default feed, sorted by
   the trend ranking in js/trending.js. Tapping any active bubble
   again returns to Trending.                                        */
/* CUSTOM ICONS — DESIGNER HANDOFF (see ICONS-HANDOFF.md):
   the site automatically loads assets/icons/{key}.png over the emoji
   for every bubble below (trending.png, backstage.png, tour.png,
   merch.png, albums.png, live.png). A designer just drops correctly
   named files into assets/icons/ — ZERO code changes needed. Emoji
   are only the fallback while no file exists.                       */
/* NAV v2 (Benton, 2026-09-09): Trending bubble removed — the mixed
   feed is simply the site's resting state (tap the logo, or tap the
   active bubble again, to come home to it). Albums + Live Shows are
   merged into one Music tab; Socials is a link panel, not a feed. */
const FILTERS = [
  /* Trending = the home button (Benton, 2026-09-11): same mixed
     feed the site opens on. Tapping it from any tab comes home. */
  { key: 'trending',  label: 'Trending',       icon: '🔥', iconImg: null },
  { key: 'backstage', label: 'Backstage Pass', icon: '🎟', iconImg: null, gated: true },
  { key: 'merch',     label: 'Merch',          icon: '🛍', iconImg: null },
  { key: 'tour',      label: 'Tour',           icon: '🗺', iconImg: null },
  { key: 'music',     label: 'Music',          icon: '♫', iconImg: 'assets/icons/albums.png' },
  { key: 'social',    label: 'Socials',        icon: '@', iconImg: null },
  /* Business Inquiries — carried over from the old homepage's
     linktree; a straight link, not a feed tab. */
  { key: 'inquiries', label: 'Business Inquiries', icon: '✉', iconImg: null, link: '/pages/contact' },
];

/* ---- Socials panel (the Socials tab) ------------------------------
   Not a feed: one clean panel of Obed's profiles. Each row is a big
   tappable icon + handle that opens the profile in a new tab.       */
const SOCIALS = [
  { key: 'instagram', label: 'Instagram', handle: '@obedpadilla_',
    url: 'https://www.instagram.com/obedpadilla_/' },
  { key: 'tiktok',    label: 'TikTok',    handle: '@obedpadilla_',
    url: 'https://www.tiktok.com/@obedpadilla_' },
  { key: 'youtube',   label: 'YouTube',   handle: '@obedpadilla_',
    url: 'https://www.youtube.com/@obedpadilla_' },
  { key: 'spotify',   label: 'Spotify',   handle: 'Obed Padilla',
    url: 'https://open.spotify.com/artist/3aXWXIWEaVHVsCCCkTLVzl' },
];
